import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// RichContentView.js's allowlist, WIDENED for the redline markup
// services/redline_service.py::render_markdown emits. This is deliberately a
// SEPARATE constant, not RichContentView's exported and extended -- keeping
// the plain view's allowlist untouched is what stops <ins>/<del> from ever
// becoming reachable through the surface that renders ordinary document
// content. See content_sanitizer.py's own comment on why <ins>/<del> are
// absent from the server allowlist: this widening is safe only because it sits
// on a component that renders content built here, from already-sanitised
// pieces, not raw user input.
const ALLOWED_TAGS = [
  'p', 'br', 'blockquote', 'code', 'pre',
  'strong', 'b', 'em', 'i', 'u', 'span',
  'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a',
  'sup',
  'ins', 'del', 'mark',
];
const ALLOWED_ATTR = [
  'href', 'title', 'style', 'target', 'rel',
  'data-author-key', 'data-author', 'data-at', 'data-comment-id',
];

// Same eight-colour ramp as global.css's --author-1..8 tokens, in the same
// order, so a hash into this array and a hash into the CSS custom properties
// agree without either side importing the other.
const AUTHOR_COLOR_VARS = [
  '--author-1', '--author-2', '--author-3', '--author-4',
  '--author-5', '--author-6', '--author-7', '--author-8',
];

// Long enough to quote a sentence or two, short enough that the anchor stays
// readable in the composer and in every comment rendered afterwards.
const MAX_QUOTE_CHARS = 300;

/** A stable colour for one author key, so the same person is always the same
 * colour within a document without knowing the roster ahead of time. */
export function authorColorVar(authorKey) {
  if (!authorKey) return null;
  let hash = 0;
  for (let i = 0; i < authorKey.length; i += 1) {
    hash = (hash * 31 + authorKey.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % AUTHOR_COLOR_VARS.length;
  return AUTHOR_COLOR_VARS[index];
}

/**
 * The text of a selection, as it exists in the STORED content.
 *
 * Deleted runs are stripped: <del> text is not present in Document.content or
 * DocumentRevision.content, and comment_service.anchor_is_present is a plain
 * substring test against exactly that, so a quote spanning a deletion would
 * post permanently detached.
 *
 * Known limitation, accepted: this is rendered Markdown, so a selection
 * crossing a formatting boundary yields display text ("bold") where the source
 * holds syntax ("**bold**"), and that comment posts detached too. That is the
 * pre-existing behaviour of quote anchoring and it degrades gracefully --
 * anchor_found comes back false, the comment survives, and the panel says the
 * context is no longer present. Do NOT try to reverse-map to source offsets;
 * offset anchoring is the design DocumentComment deliberately rejected.
 */
function quoteFromSelection(range) {
  const fragment = range.cloneContents();
  fragment.querySelectorAll('del').forEach((el) => el.remove());
  const text = fragment.textContent || '';
  return text.replace(/\s+/g, ' ').trim().slice(0, MAX_QUOTE_CHARS);
}

/**
 * Renders a computed redline: Markdown carrying <ins>/<del> islands, as
 * returned by GET .../redline, .../redline/preview or a frozen
 * DocumentVersion.content_redline.
 *
 * NEVER pass Document.content or DocumentRevision.content here -- those are
 * plain content with no markup of ours, and belong to RichContentView. This
 * component exists only for text that has already been through
 * redline_service.render_markdown.
 *
 * `colorByAuthor`: when true, each <ins>/<del> is additionally tinted by
 * whoever wrote it (data-author-key), rather than the flat blue/red the CSS
 * applies by default -- the per-span attribution the redline is built for.
 * Leave it OFF wherever the red-strikethrough / blue-addition reading is the
 * point (the edit pages): the tint REPLACES that colouring rather than adding
 * to it. Authorship is still available there, on hover -- see below.
 *
 * `onSelectQuote`: when given, selecting text offers a "Comment on this"
 * action that hands the quote back, so the page mounting both this and
 * CommentsPanel can pre-fill the composer (its pendingAnchor prop).
 */
function RedlineView({ redlineMarkdown, colorByAuthor = false, onSelectQuote }) {
  const html = useMemo(() => {
    const rendered = marked.parse(redlineMarkdown || '', { breaks: true });
    return DOMPurify.sanitize(rendered, { ALLOWED_TAGS, ALLOWED_ATTR });
  }, [redlineMarkdown]);

  const containerRef = React.useRef(null);
  const hostRef = React.useRef(null);
  const [selection, setSelection] = React.useState(null);

  // Applied post-render rather than baked into the sanitised HTML string:
  // both the colour and the tooltip are presentational, and DOMPurify has no
  // opinion about either, so this stays in JS where it's easy to read.
  React.useEffect(() => {
    if (!containerRef.current) return;
    const spans = containerRef.current.querySelectorAll('ins[data-author-key], del[data-author-key]');
    spans.forEach((el) => {
      // Who made this edit and when, on hover. This is how a pane running
      // WITHOUT colorByAuthor still names the people associated with a change.
      const who = el.getAttribute('data-author');
      const at = el.getAttribute('data-at');
      if (who) {
        const verb = el.tagName === 'INS' ? 'Added' : 'Removed';
        el.title = at
          ? `${verb} by ${who} · ${new Date(at).toLocaleString()}`
          : `${verb} by ${who}`;
      }
      if (!colorByAuthor) return;
      const varName = authorColorVar(el.getAttribute('data-author-key'));
      if (varName) {
        el.setAttribute('data-author-color', '');
        el.style.setProperty('--redline-author-color', `var(${varName})`);
      }
    });
  }, [html, colorByAuthor]);

  // A fresh render is a different document; any offered action is stale.
  React.useEffect(() => { setSelection(null); }, [html]);

  const handleMouseUp = () => {
    if (!onSelectQuote) return;
    const sel = window.getSelection();
    const root = containerRef.current;
    const host = hostRef.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !root || !host) {
      setSelection(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }
    const quote = quoteFromSelection(range);
    if (!quote) {
      setSelection(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    setSelection({
      quote,
      top: rect.bottom - hostRect.top + 6,
      left: Math.max(0, rect.left - hostRect.left),
    });
  };

  return (
    <div className="redline-selectable" ref={hostRef} onMouseUp={handleMouseUp}>
      <div
        ref={containerRef}
        className="redline-view"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {selection && (
        <button
          type="button"
          className="redline-comment-action"
          style={{ top: `${selection.top}px`, left: `${selection.left}px` }}
          // Without this the click collapses the selection before onClick runs,
          // and the quote is gone by the time we want to read it.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onSelectQuote(selection.quote);
            setSelection(null);
            const sel = window.getSelection();
            if (sel) sel.removeAllRanges();
          }}
        >
          💬 Comment on this
        </button>
      )}
    </div>
  );
}

export default RedlineView;
